import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Video, Wand2, Loader2, Play, Download, 
  Clock, Image, LayoutTemplate, Sparkles, CheckCircle, 
  XCircle, RefreshCw, Film, ImagePlus, Palette, GalleryHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn, formatTimeAgo } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Video size options
const VIDEO_SIZES = [
  { id: '1280x720', name: 'Landscape (16:9)', desc: 'Best for YouTube, Facebook' },
  { id: '1024x1024', name: 'Square (1:1)', desc: 'Best for Instagram feed' },
  { id: '1024x1792', name: 'Portrait (9:16)', desc: 'Best for TikTok, Reels, Stories' },
];

// Video duration options
const VIDEO_DURATIONS = [
  { id: 4, name: '4 seconds', desc: 'Quick clip' },
  { id: 8, name: '8 seconds', desc: 'Standard' },
  { id: 12, name: '12 seconds', desc: 'Extended' },
];

// Image size options
const IMAGE_SIZES = [
  { id: '1024x1024', name: 'Square (1:1)', desc: 'Best for profiles, social' },
  { id: '1536x1024', name: 'Landscape (3:2)', desc: 'Best for covers, banners' },
  { id: '1024x1536', name: 'Portrait (2:3)', desc: 'Best for stories, pins' },
];

// Image style options
const IMAGE_STYLES = [
  { id: 'vivid', name: 'Vivid', desc: 'Bold and colorful' },
  { id: 'natural', name: 'Natural', desc: 'Realistic look' },
];

// Image template prompts
const IMAGE_TEMPLATES = [
  { 
    id: 'portrait', 
    name: 'Portrait',
    icon: '👤',
    prompt: 'A professional portrait of a person with soft lighting and a blurred background, high quality photography'
  },
  { 
    id: 'landscape', 
    name: 'Landscape',
    icon: '🏞️',
    prompt: 'A stunning landscape with mountains, lakes, and a beautiful sky during golden hour'
  },
  { 
    id: 'abstract', 
    name: 'Abstract Art',
    icon: '🎨',
    prompt: 'Colorful abstract digital art with flowing shapes, vibrant gradients, and modern design'
  },
  { 
    id: 'anime', 
    name: 'Anime Style',
    icon: '✨',
    prompt: 'Beautiful anime-style illustration with detailed character design, soft colors, and expressive eyes'
  },
  { 
    id: 'product', 
    name: 'Product Shot',
    icon: '📦',
    prompt: 'Professional product photography on white background with soft shadows and perfect lighting'
  },
  { 
    id: 'fantasy', 
    name: 'Fantasy',
    icon: '🐉',
    prompt: 'Epic fantasy scene with magical elements, dragons, castles, and mystical lighting'
  },
];

// Template prompts
const PROMPT_TEMPLATES = [
  { 
    id: 'nature', 
    name: 'Nature Scene',
    icon: '🌿',
    prompt: 'A beautiful serene nature scene with flowing water, lush green trees, and gentle sunlight filtering through the leaves'
  },
  { 
    id: 'city', 
    name: 'City Life',
    icon: '🌆',
    prompt: 'A vibrant modern cityscape at golden hour with busy streets, neon lights, and diverse people walking'
  },
  { 
    id: 'abstract', 
    name: 'Abstract Art',
    icon: '🎨',
    prompt: 'Colorful abstract shapes morphing and flowing in a hypnotic dance with vibrant gradients'
  },
  { 
    id: 'ocean', 
    name: 'Ocean Waves',
    icon: '🌊',
    prompt: 'Peaceful ocean waves crashing on a sandy beach at sunset with seagulls flying overhead'
  },
  { 
    id: 'space', 
    name: 'Space Journey',
    icon: '🚀',
    prompt: 'A mesmerizing journey through colorful nebulas and distant galaxies with stars twinkling'
  },
  { 
    id: 'food', 
    name: 'Food Video',
    icon: '🍕',
    prompt: 'Delicious gourmet food being prepared in a professional kitchen with steam and sizzling sounds'
  },
];

export default function CreatorStudioPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Video states
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1280x720');
  const [duration, setDuration] = useState(4);
  const [generations, setGenerations] = useState([]);
  const [polling, setPolling] = useState({});
  
  // Image states
  const [imageLoading, setImageLoading] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageSize, setImageSize] = useState('1024x1024');
  const [imageStyle, setImageStyle] = useState('vivid');
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Tab state - main tabs and sub-tabs
  const [mainTab, setMainTab] = useState('video'); // 'video' or 'image'
  const [activeTab, setActiveTab] = useState('create');

  useEffect(() => {
    fetchGenerations();
    fetchGeneratedImages();
  }, []);

  const fetchGenerations = async () => {
    try {
      const response = await axios.get(`${API}/ai/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGenerations(response.data);
      
      // Start polling for processing videos
      response.data.forEach(gen => {
        if (gen.status === 'processing') {
          startPolling(gen.id);
        }
      });
    } catch (error) {
      console.error('Failed to fetch generations:', error);
    }
  };

  const startPolling = (generationId) => {
    if (polling[generationId]) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/ai/video/${generationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status !== 'processing') {
          clearInterval(interval);
          setPolling(prev => {
            const newPolling = { ...prev };
            delete newPolling[generationId];
            return newPolling;
          });
          
          // Update the generation in the list
          setGenerations(prev => prev.map(g => 
            g.id === generationId ? response.data : g
          ));
          
          if (response.data.status === 'completed') {
            toast.success('Video generated successfully!');
          } else {
            toast.error('Video generation failed');
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);
    
    setPolling(prev => ({ ...prev, [generationId]: interval }));
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/video/generate`, 
        { prompt, size, duration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Video generation started! This may take 2-5 minutes.');
      
      // Add to generations list
      const newGen = {
        id: response.data.generation_id,
        prompt,
        size,
        duration,
        status: 'processing',
        created_at: new Date().toISOString()
      };
      setGenerations(prev => [newGen, ...prev]);
      
      // Start polling
      startPolling(response.data.generation_id);
      
      // Switch to history tab
      setActiveTab('history');
      setPrompt('');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start generation');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    setPrompt(template.prompt);
    toast.success(`Template "${template.name}" loaded!`);
  };

  const applyImageTemplate = (template) => {
    setImagePrompt(template.prompt);
    toast.success(`Template "${template.name}" loaded!`);
  };

  const fetchGeneratedImages = async () => {
    try {
      const response = await axios.get(`${API}/ai/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedImages(response.data);
    } catch (error) {
      console.error('Failed to fetch images:', error);
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setImageLoading(true);
    try {
      const response = await axios.post(`${API}/ai/image/generate`, 
        { prompt: imagePrompt, size: imageSize, style: imageStyle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Image generated successfully!');
      
      // Add to images list
      const newImage = {
        id: response.data.image_url?.split('_').pop()?.replace('.png', '') || Date.now().toString(),
        prompt: imagePrompt,
        image_url: response.data.image_url,
        image_base64: response.data.image_base64,
        created_at: new Date().toISOString()
      };
      setGeneratedImages(prev => [newImage, ...prev]);
      
      // Show the generated image
      setSelectedImage(newImage);
      
      // Switch to gallery tab
      setActiveTab('history');
      setImagePrompt('');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setImageLoading(false);
    }
  };

  const downloadImage = (image) => {
    const link = document.createElement('a');
    if (image.image_base64) {
      link.href = `data:image/png;base64,${image.image_base64}`;
    } else {
      link.href = `${process.env.REACT_APP_BACKEND_URL}${image.image_url}`;
    }
    link.download = `fityh_ai_image_${image.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="creator-studio-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-bold text-lg">Creator Studio</h1>
            <p className="text-xs text-muted-foreground">AI Content Generation</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Main Type Toggle - Video vs Image */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => { setMainTab('video'); setActiveTab('create'); }}
            className={cn(
              "p-4 rounded-2xl border-2 flex items-center justify-center gap-3 transition-all",
              mainTab === 'video' 
                ? "border-primary bg-primary/10" 
                : "border-border hover:border-primary/50"
            )}
            data-testid="video-tab-btn"
          >
            <Video className="w-6 h-6" />
            <span className="font-semibold">AI Video</span>
          </button>
          <button
            onClick={() => { setMainTab('image'); setActiveTab('create'); }}
            className={cn(
              "p-4 rounded-2xl border-2 flex items-center justify-center gap-3 transition-all",
              mainTab === 'image' 
                ? "border-primary bg-primary/10" 
                : "border-border hover:border-primary/50"
            )}
            data-testid="image-tab-btn"
          >
            <ImagePlus className="w-6 h-6" />
            <span className="font-semibold">AI Image</span>
          </button>
        </div>

        {/* Sub Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'create' ? 'default' : 'outline'}
            className="flex-1 rounded-full"
            onClick={() => setActiveTab('create')}
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Create {mainTab === 'video' ? 'Video' : 'Image'}
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'outline'}
            className="flex-1 rounded-full"
            onClick={() => setActiveTab('history')}
          >
            {mainTab === 'video' ? <Film className="w-4 h-4 mr-2" /> : <GalleryHorizontal className="w-4 h-4 mr-2" />}
            {mainTab === 'video' ? `My Videos (${generations.length})` : `My Images (${generatedImages.length})`}
          </Button>
        </div>

        {/* ========== VIDEO SECTION ========== */}
        {mainTab === 'video' && (
          <>
            {/* Create Video Tab */}
            {activeTab === 'create' && (
              <div className="space-y-6">
                {/* AI Info Card */}
                <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
                        <Video className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">AI Video Generator</h2>
                        <p className="text-muted-foreground">Powered by Sora 2</p>
                        <p className="text-sm text-muted-foreground mt-1">Generate stunning videos from text descriptions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Templates */}
                <div>
                  <Label className="text-lg font-semibold mb-3 block">Quick Templates</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {PROMPT_TEMPLATES.map(template => (
                      <button
                        key={template.id}
                        onClick={() => applyTemplate(template)}
                        className="p-4 rounded-2xl bg-secondary hover:bg-secondary/80 text-left transition-all hover:scale-105"
                        data-testid={`template-${template.id}`}
                      >
                        <span className="text-3xl block mb-2">{template.icon}</span>
                        <span className="font-medium text-sm">{template.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Prompt Input */}
                <div>
                  <Label htmlFor="prompt" className="text-lg font-semibold mb-3 block">
                    Describe Your Video
                  </Label>
                  <Textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="E.g., A beautiful sunset over the ocean with dolphins jumping out of the water..."
                    className="min-h-[120px] rounded-2xl resize-none"
                    data-testid="video-prompt"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Be descriptive! Include details about scene, action, colors, mood, and style.
                  </p>
                </div>

                {/* Size Selection */}
                <div>
                  <Label className="text-lg font-semibold mb-3 block">Video Size</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {VIDEO_SIZES.map(s => (
                      <button
                        key={s.id}
                        onClick={() => setSize(s.id)}
                        className={cn(
                          "p-4 rounded-2xl border-2 text-left transition-all",
                          size === s.id 
                            ? "border-primary bg-primary/10" 
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <p className="font-semibold text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration Selection */}
                <div>
                  <Label className="text-lg font-semibold mb-3 block">Duration</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {VIDEO_DURATIONS.map(d => (
                      <button
                        key={d.id}
                        onClick={() => setDuration(d.id)}
                        className={cn(
                          "p-4 rounded-2xl border-2 text-left transition-all",
                          duration === d.id 
                            ? "border-primary bg-primary/10" 
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <p className="font-semibold">{d.name}</p>
                        <p className="text-xs text-muted-foreground">{d.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim()}
                  className="w-full rounded-full h-14 text-lg"
                  data-testid="generate-video-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Starting Generation...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Generate Video
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Generation takes 2-5 minutes. You'll be notified when ready.
                </p>
              </div>
            )}

            {/* Video History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                {generations.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-lg font-medium">No videos yet</p>
                      <p className="text-muted-foreground mt-1">Create your first AI video!</p>
                      <Button 
                        className="mt-4 rounded-full"
                        onClick={() => setActiveTab('create')}
                      >
                        <Wand2 className="w-4 h-4 mr-2" />
                        Create Video
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {generations.map(gen => (
                      <Card key={gen.id} data-testid={`generation-${gen.id}`}>
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            {/* Video Preview */}
                            <div className="w-32 h-20 rounded-xl bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                              {gen.status === 'completed' && gen.video_url ? (
                                <video 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${gen.video_url}`}
                                  className="w-full h-full object-cover"
                                  muted
                                />
                              ) : gen.status === 'processing' ? (
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                              ) : (
                                <XCircle className="w-8 h-8 text-destructive" />
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {gen.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                {gen.status === 'processing' && <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />}
                                {gen.status === 'failed' && <XCircle className="w-4 h-4 text-destructive" />}
                                <span className={cn(
                                  "text-sm font-medium capitalize",
                                  gen.status === 'completed' && "text-green-500",
                                  gen.status === 'processing' && "text-yellow-500",
                                  gen.status === 'failed' && "text-destructive"
                                )}>
                                  {gen.status}
                                </span>
                              </div>
                              <p className="text-sm line-clamp-2">{gen.prompt}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span>{gen.size}</span>
                                <span>•</span>
                                <span>{gen.duration}s</span>
                                <span>•</span>
                                <span>{formatTimeAgo(gen.created_at)}</span>
                              </div>
                            </div>
                            
                            {/* Actions */}
                            {gen.status === 'completed' && gen.video_url && (
                              <div className="flex flex-col gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${gen.video_url}`, '_blank')}
                                >
                                  <Play className="w-4 h-4 mr-1" />
                                  Play
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  asChild
                                >
                                  <a href={`${process.env.REACT_APP_BACKEND_URL}${gen.video_url}`} download>
                                    <Download className="w-4 h-4 mr-1" />
                                    Save
                                  </a>
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {gen.error && (
                            <p className="mt-2 text-sm text-destructive">{gen.error}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ========== IMAGE SECTION ========== */}
        {mainTab === 'image' && (
          <>
            {/* Create Image Tab */}
            {activeTab === 'create' && (
              <div className="space-y-6">
                {/* AI Info Card */}
                <Card className="bg-gradient-to-br from-violet-500/20 to-violet-500/5 border-violet-500/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center">
                        <ImagePlus className="w-8 h-8 text-violet-500" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">AI Image Generator</h2>
                        <p className="text-muted-foreground">Powered by GPT Image 1</p>
                        <p className="text-sm text-muted-foreground mt-1">Create stunning images from text descriptions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Image Templates */}
                <div>
                  <Label className="text-lg font-semibold mb-3 block">Quick Templates</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {IMAGE_TEMPLATES.map(template => (
                      <button
                        key={template.id}
                        onClick={() => applyImageTemplate(template)}
                        className="p-4 rounded-2xl bg-secondary hover:bg-secondary/80 text-left transition-all hover:scale-105"
                        data-testid={`img-template-${template.id}`}
                      >
                        <span className="text-3xl block mb-2">{template.icon}</span>
                        <span className="font-medium text-sm">{template.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image Prompt Input */}
                <div>
                  <Label htmlFor="imagePrompt" className="text-lg font-semibold mb-3 block">
                    Describe Your Image
                  </Label>
                  <Textarea
                    id="imagePrompt"
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="E.g., A majestic lion standing on a rock at sunset, with golden light illuminating its mane..."
                    className="min-h-[120px] rounded-2xl resize-none"
                    data-testid="image-prompt"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Be specific! Include details about subject, style, lighting, colors, and mood.
                  </p>
                </div>

                {/* Image Size Selection */}
                <div>
                  <Label className="text-lg font-semibold mb-3 block">Image Size</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {IMAGE_SIZES.map(s => (
                      <button
                        key={s.id}
                        onClick={() => setImageSize(s.id)}
                        className={cn(
                          "p-4 rounded-2xl border-2 text-left transition-all",
                          imageSize === s.id 
                            ? "border-violet-500 bg-violet-500/10" 
                            : "border-border hover:border-violet-500/50"
                        )}
                      >
                        <p className="font-semibold text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Style Selection */}
                <div>
                  <Label className="text-lg font-semibold mb-3 block">Style</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {IMAGE_STYLES.map(st => (
                      <button
                        key={st.id}
                        onClick={() => setImageStyle(st.id)}
                        className={cn(
                          "p-4 rounded-2xl border-2 text-left transition-all",
                          imageStyle === st.id 
                            ? "border-violet-500 bg-violet-500/10" 
                            : "border-border hover:border-violet-500/50"
                        )}
                      >
                        <p className="font-semibold">{st.name}</p>
                        <p className="text-xs text-muted-foreground">{st.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerateImage}
                  disabled={imageLoading || !imagePrompt.trim()}
                  className="w-full rounded-full h-14 text-lg bg-violet-600 hover:bg-violet-700"
                  data-testid="generate-image-btn"
                >
                  {imageLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Image...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Image
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Generation usually takes 10-30 seconds.
                </p>
              </div>
            )}

            {/* Image Gallery Tab */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                {generatedImages.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-lg font-medium">No images yet</p>
                      <p className="text-muted-foreground mt-1">Create your first AI image!</p>
                      <Button 
                        className="mt-4 rounded-full bg-violet-600 hover:bg-violet-700"
                        onClick={() => setActiveTab('create')}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Create Image
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Image Preview Modal */}
                    {selectedImage && (
                      <div 
                        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                        onClick={() => setSelectedImage(null)}
                      >
                        <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
                          <img 
                            src={selectedImage.image_base64 
                              ? `data:image/png;base64,${selectedImage.image_base64}` 
                              : `${process.env.REACT_APP_BACKEND_URL}${selectedImage.image_url}`}
                            alt={selectedImage.prompt}
                            className="max-w-full max-h-[80vh] object-contain rounded-2xl"
                          />
                          <div className="absolute top-4 right-4 flex gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              className="rounded-full"
                              onClick={() => downloadImage(selectedImage)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="rounded-full"
                              onClick={() => setSelectedImage(null)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent rounded-b-2xl">
                            <p className="text-white text-sm line-clamp-2">{selectedImage.prompt}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {generatedImages.map(img => (
                        <div 
                          key={img.id} 
                          className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer bg-secondary"
                          onClick={() => setSelectedImage(img)}
                          data-testid={`ai-image-${img.id}`}
                        >
                          <img 
                            src={img.image_base64 
                              ? `data:image/png;base64,${img.image_base64}` 
                              : `${process.env.REACT_APP_BACKEND_URL}${img.image_url}`}
                            alt={img.prompt}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <p className="text-white text-xs line-clamp-2">{img.prompt}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="absolute top-2 right-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => { e.stopPropagation(); downloadImage(img); }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
